"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";

import styles from "./HeroBorder.module.scss";
import className from "classnames/bind";
const cx = className.bind(styles);

type HeroBorderProps = {
  className?: string;
  small?: boolean;
  radiusPx?: number; // Optional override
};

export default function HeroBorder({ className = "", small, radiusPx = 10 }: HeroBorderProps) {

  // Track both elements with state + callback refs so the effect re-runs when either mounts
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const rootRef = useCallback((el: HTMLDivElement | null) => setRootEl(el), []);

  const [svgEl, setSvgEl] = useState<SVGSVGElement | null>(null);
  const svgRef = useCallback((el: SVGSVGElement | null) => setSvgEl(el), []);

  // -------------------- Layout and SVG Masking

  useEffect(() => {
    const svg = svgEl;
    const root = rootEl;
    // Wait until both elements are mounted before building the SVG/mask/layout
    if (!svg || !root) return;

    const ns = "http://www.w3.org/2000/svg";

    // Unique id per instance to avoid collisions
    const maskId = `heroFrameMask-${Math.random().toString(36).slice(2, 9)}`;

    // Build once
    const defs = document.createElementNS(ns, "defs");
    const mask = document.createElementNS(ns, "mask");
    mask.id = maskId;

    // Ensure mask uses absolute SVG user-space coordinates (so our px rects line up)
    mask.setAttribute("maskUnits", "userSpaceOnUse");
    mask.setAttribute("maskContentUnits", "userSpaceOnUse");

    // Mask rect
    const outerRect = document.createElementNS(ns, "rect");
    outerRect.setAttribute("fill", "white");

    // Inner rect is cutout (black)
    const innerRect = document.createElementNS(ns, "rect");
    innerRect.setAttribute("fill", "black");

    // Build mask
    mask.appendChild(outerRect);
    mask.appendChild(innerRect);
    defs.appendChild(mask);

    // Painted rect (uses mask)
    const paint = document.createElementNS(ns, "rect");
    paint.setAttribute("fill", "var(--highlight-color)");
    paint.setAttribute("mask", `url(#${maskId})`);

    // Wipe and append
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    svg.appendChild(defs);
    svg.appendChild(paint);

    // Compute thickness based on size
    function computeThickness() {
      const width = window.innerWidth;
      return small ? 8 : width >= 768 ? 16 : 12;
    }

    // Layout function (called on size changes)
    function layout() {
      if (!root) return;

      // Get the size of the root element
      const rect = {
        width: root.offsetWidth,
        height: root.offsetHeight,
      }
      // Note: We can not use getBoundingClientRect() here because during the Hero reveal
      // animation this returns 0,0 for width/height. offsetWidth/Height works correctly. -Gabe

      // Keep fractional values (no rounding) so SVG maps exactly to the CSS box
      const w = rect.width;
      const h = rect.height;
      const thickness = computeThickness();

      if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) {
        // Invalid size, skip layout
        return;
      }

      // Update mask bounds so mask coords match the painted rect
      mask.setAttribute("x", "0");
      mask.setAttribute("y", "0");
      mask.setAttribute("width", String(Math.ceil(w)));
      mask.setAttribute("height", String(Math.ceil(h)));

      // Ensure the SVG scales exactly to the CSS layout box
      svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
      svg.setAttribute("preserveAspectRatio", "none");
      svg.setAttribute("width", "100%");
      svg.setAttribute("height", "100%");

      outerRect.setAttribute("x", "0");
      outerRect.setAttribute("y", "0");
      outerRect.setAttribute("width", String(w));
      outerRect.setAttribute("height", String(h));

      innerRect.setAttribute("x", String(thickness));
      innerRect.setAttribute("y", String(thickness));
      innerRect.setAttribute("width", String(Math.max(0, w - 2 * thickness)));
      innerRect.setAttribute("height", String(Math.max(0, h - 2 * thickness)));
      innerRect.setAttribute("rx", String(radiusPx));
      innerRect.setAttribute("ry", String(radiusPx));

      paint.setAttribute("x", "0");
      paint.setAttribute("y", "0");
      paint.setAttribute("width", String(w));
      paint.setAttribute("height", String(h));
    }

    // Observe element size changes (keeps layout in sync with container)
    const ro = new ResizeObserver(layout);
    // observe the container (we read container offsetWidth/offsetHeight in layout)
    ro.observe(root);

    // Also recompute on window resize (so thickness calc can use window.innerWidth)
    window.addEventListener("resize", layout);

    // Initial layout
    layout();

    // Cleanup
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", layout);
    };
  }, [radiusPx, small, svgEl, rootEl]);

  // -------------------- Render

  return (
    <div className={`${cx("hero-border")} ${className} ${small ? `small` : ""}`} ref={rootRef}>
      <svg ref={svgRef} aria-hidden="true" />
    </div>
  );

}
